<script setup lang="ts">
import { ref, computed } from 'vue'
import RPGStatPresentor from './RPGStatPresentor.vue'
import CodexHeading from '@/components/CodexRail/CodexHeading.vue'
import SwitchBaseValuesModal from '@/components/Modal/SwitchBaseValuesModal.vue'
import { useI18n } from 'vue-i18n'

interface Stat {
  id: string
  name: string
  dice_rolls: any[]
  additional_value: number
  base_value?: number
}

interface Props {
  stats: Stat[]
  title?: string
  showUpgrades?: boolean
  availableUpgradePoints?: number
  allowResetBaseStats?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Character Stats',
  showUpgrades: true,
  availableUpgradePoints: 0,
  allowResetBaseStats: false
})

const emit = defineEmits<{
  upgradeStat: [statId: string]
  statUpgraded: [statId: string, newValue: number]
  switchBaseStats: [{ fromStatId: string, toStatId: string }]
}>()

const { t } = useI18n()

const upgradePoints = ref(props.availableUpgradePoints)
const showModal = ref(false)
const preSelectedStat = ref<Stat | null>(null)

const totalStatsValue = computed(() => {
  return props.stats.reduce((total, stat) => {
    const baseValue = stat.base_value || 0
    return total + baseValue + stat.additional_value
  }, 0)
})

const requestStatSwitch = (statId: string) => {
  preSelectedStat.value = props.stats.find((stat) => stat.id === statId) || null
  openModal()
}

const openModal = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const handleSwitch = ({ fromStatId, toStatId }: { fromStatId: string, toStatId: string }) => {
  emit('switchBaseStats', { fromStatId, toStatId })
  closeModal()
}
</script>

<template>
  <div class="rpg-stats-container">
    <div class="stats-header">
      <CodexHeading :label="title" tone="cyan" spine="head"/>

      <div class="stats-summary" v-if="showUpgrades">
        <div class="summary-cell">
          <span class="summary-label">{{ t('player.profile.upgradePoints') }}</span>
          <span class="summary-value summary-value--points" :class="{ 'is-idle': upgradePoints === 0 }">
            {{ upgradePoints }}
          </span>
        </div>
        <span class="summary-divider" aria-hidden="true"></span>
        <div class="summary-cell">
          <span class="summary-label">{{ t('player.profile.totalStats') }}</span>
          <span class="summary-value">{{ totalStatsValue }}</span>
        </div>
      </div>
    </div>

    <div class="stats-list">
      <RPGStatPresentor
        v-for="stat in stats"
        :key="stat.id"
        :stat="stat"
        :allowResetBaseStats="allowResetBaseStats"
        @switchBaseStats="requestStatSwitch"
        class="stat-item"
      />
    </div>

    <div class="stats-footer" v-if="showUpgrades && upgradePoints === 0">
      <p class="no-points-message">{{ t('player.profile.noUpgradePoints') }}</p>
    </div>

    <!-- Modal for stat swapping -->
    <SwitchBaseValuesModal
      :stats="stats"
      :isOpen="showModal && allowResetBaseStats"
      :initialFromStatId="preSelectedStat?.id"
      @switchBaseValues="handleSwitch"
      @closeModal="closeModal"
    />
  </div>
</template>

<style scoped>
.rpg-stats-container {
  --hud-cyan: #3ee9f0;
  --hud-line: rgba(0, 255, 255, 0.18);
  --hud-slab: rgba(11, 18, 24, 0.72);
  --hud-text: #d3e0e6;
  --hud-text-dim: #7f929c;
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 0;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}

.stats-header {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.45rem;
}

.stats-summary {
  display: flex;
  align-items: stretch;
  background: var(--hud-slab);
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
}

.summary-cell {
  display: flex;
  flex: 1 1 0;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.32rem 0.6rem;
}

.summary-divider {
  flex: 0 0 1px;
  background: var(--hud-line);
}

.summary-label {
  overflow: hidden;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.54rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--hud-text-dim);
}

.summary-value {
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--hud-text);
}

.summary-value--points {
  color: var(--hud-cyan);
  text-shadow: 0 0 0.5rem rgba(62, 233, 240, 0.35);
}

.summary-value--points.is-idle {
  color: var(--hud-text-dim);
  text-shadow: none;
}

.stats-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.2rem;
  min-height: 0;
  /* Keeps a long attribute list inside the panel instead of growing the dialog. */
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(62, 233, 240, 0.3) transparent;
}

.stats-list::-webkit-scrollbar {
  width: 4px;
}

.stats-list::-webkit-scrollbar-thumb {
  background: rgba(62, 233, 240, 0.3);
  border-radius: 2px;
}

.stats-footer {
  flex: 0 0 auto;
  padding-top: 0.4rem;
  border-top: 1px solid var(--hud-line);
  text-align: center;
}

.no-points-message {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.52rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

@media (max-width: 820px) {
  .stats-list {
    overflow-y: visible;
  }
}
</style>
